import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  Cell,
} from 'recharts';
import { ChildProfile } from '../../models/childProfile';
import { TaxonomyPayload } from '../../models/taxonomy';
import { PostDataService } from '../../services/postDataService';
import {
  InsightsDataService,
  InsightsSummary,
  SEND_DOMAINS,
  domainForSlug,
  INDICATIONS,
  INDICATION_LABELS,
  INDICATION_COLOURS,
  EhcpIndication,
  AbcValenceFilter,
} from '../../services/insightsDataService';
import { toDateString, getMonday } from '../../services/dateUtils';
import TargetGapAnalysis from './TargetGapAnalysis';
import BehaviourInsights from './BehaviourInsights';
// `.pill-toolbar__segment` — the shared toggle chrome used by the Posts
// toolbar and Reports. Imported here so this screen declares the dependency
// rather than relying on TopTabs having pulled it in.
import '../common/topTabs.scss';
import './charts.scss';

// ── Helpers ──────────────────────────────────────────────────────────────────

function addDays(d: Date, n: number): Date {
  const c = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  c.setDate(c.getDate() + n);
  return c;
}

function shortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00');
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

// Continuous list of ISO week-start (Monday) dates spanning the range, so weekly
// line charts show zero-weeks as gaps in the trend rather than collapsing them.
// Mondays match Postgres date_trunc('week', …) used for the ehcp_by_week series.
function weekStartsInRange(fromDate: string, toDate: string): string[] {
  const out: string[] = [];
  let cur = getMonday(new Date(fromDate + 'T00:00'));
  const end = new Date(toDate + 'T00:00');
  while (cur.getTime() <= end.getTime()) {
    out.push(toDateString(cur));
    cur = addDays(cur, 7);
  }
  return out;
}

// 'count' = raw observation counts; 'share' = composition as a % of the relevant
// total, so periods/weeks with more or fewer logged posts compare fairly — the
// charts then reflect the *mix* of observations, not how much was logged.
type Measure = 'count' | 'share';
const pct = (n: number, total: number): number => (total > 0 ? Math.round((n / total) * 100) : 0);

const AXIS = '#7e8a78';
const GRID = '#e6e0d2';

// recharts 3.x types some components as returning `ReactNode` (incl. undefined),
// which React 19's JSX typing rejects as an element. The polar-axis components
// trip this; alias them through a permissive FC cast (same pragmatic approach as
// the `as any` formatter casts used elsewhere in the charts).
const AngleAxis = PolarAngleAxis as unknown as React.FC<any>;
const RadiusAxis = PolarRadiusAxis as unknown as React.FC<any>;

// ── Range options ─────────────────────────────────────────────────────────────
// EHCP progress is best read over a term-or-longer window (Gemini: default the
// progress timeline to ~12 weeks). Offer monthly / termly / annual.

interface RangeOption { key: string; label: string; weeks: number; }
const RANGE_OPTIONS: RangeOption[] = [
  { key: '4',  label: '4 wks',   weeks: 4  },
  { key: '12', label: '12 wks',  weeks: 12 },
  { key: '52', label: 'Full year', weeks: 52 },
];

// ── Component ──────────────────────────────────────────────────────────────────

interface Props {
  childProfiles: ChildProfile[];
  childIdFilter: string | null;
  /** Full-area child picker, supplied while this section is waiting on a
   *  selection (or when the user asked to browse children full screen). */
  childPicker?: React.ReactNode;
}

// EHCP targets (gap analysis) isn't finished yet, so its tab is hidden rather
// than removed: `TargetGapAnalysis` and the `view === 'targets'` branch below
// are left wired up, so flipping this back to `true` is the whole re-enable.
const SHOW_EHCP_TARGETS = false;

type InsightsView = 'charts' | 'targets' | 'behaviour';

const VIEW_TABS: { key: InsightsView; label: string }[] = [
  { key: 'charts', label: 'Trends' },
  { key: 'targets', label: 'EHCP targets' },
  { key: 'behaviour', label: 'Behaviour' },
];

const ChildInsights: React.FC<Props> = ({ childProfiles, childIdFilter, childPicker }) => {
  const [range, setRange] = useState<RangeOption>(RANGE_OPTIONS[1]); // charts/targets: 12 wks
  // Behaviour keeps its own range (challenging episodes are sparse → default a
  // longer window), so switching tabs doesn't disturb the EHCP range and vice versa.
  const [behaviourRange, setBehaviourRange] = useState<RangeOption>(RANGE_OPTIONS[2]); // 52 wks
  const [valence, setValence] = useState<AbcValenceFilter>('negative');
  const [summary, setSummary] = useState<InsightsSummary | null>(null);
  const [taxonomy, setTaxonomy] = useState<TaxonomyPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Drill-down: a SEND area (domain key) to explore over time, and optionally a
  // single sub-domain slug within it to narrow the trend further.
  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  const [selectedSubdomain, setSelectedSubdomain] = useState<string | null>(null);
  // Compare by composition (%) by default so logging volume / attendance doesn't
  // skew the picture; 'count' shows raw volume for when that's wanted.
  const [measure, setMeasure] = useState<Measure>('share');
  // Sub-view: EHCP trend charts, EHCP-target gap-analysis, or ABC behaviour.
  const [view, setView] = useState<InsightsView>('charts');
  const tabs = useMemo(
    () => VIEW_TABS.filter(t => t.key !== 'targets' || SHOW_EHCP_TARGETS),
    [],
  );

  // The range control drives whichever view is active (behaviour has its own).
  const activeRange = view === 'behaviour' ? behaviourRange : range;
  const setActiveRange = (opt: RangeOption) => (view === 'behaviour' ? setBehaviourRange(opt) : setRange(opt));
  // Behaviour buckets monthly by default; weekly only for the short ranges.
  const behaviourBucket: 'week' | 'month' = activeRange.weeks > 12 ? 'month' : 'week';

  const childId = childIdFilter ? Number(childIdFilter) : null;
  const selectedChild = useMemo(
    () => (childId == null ? null : childProfiles.find(c => c.id === childId) ?? null),
    [childId, childProfiles],
  );

  // Date window: end on Sunday of the current week, start `weeks * 7` days back.
  const { fromDate, toDate, rangeLabel } = useMemo(() => {
    const monday = getMonday(new Date());
    const end = addDays(monday, 6);
    const start = addDays(monday, -((activeRange.weeks - 1) * 7));
    return {
      fromDate: toDateString(start),
      toDate: toDateString(end),
      rangeLabel: `${shortDate(toDateString(start))} – ${shortDate(toDateString(end))}`,
    };
  }, [activeRange]);

  // Taxonomy (sub-domain labels for the breakdown chart) — fetched once.
  useEffect(() => {
    let cancelled = false;
    PostDataService.getTaxonomy()
      .then(t => { if (!cancelled) setTaxonomy(t); })
      .catch(err => console.error('Failed to load taxonomy for insights', err));
    return () => { cancelled = true; };
  }, []);

  // Summary (incl. weekly series) — refetch on child / range change.
  useEffect(() => {
    if (childId == null) { setSummary(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(false);
    InsightsDataService.fetchSummary(childId, fromDate, toDate, { weekly: true })
      .then(data => { if (!cancelled) setSummary(data); })
      .catch(err => {
        console.error('Failed to load insights summary', err);
        if (!cancelled) setError(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [childId, fromDate, toDate]);

  // Drop any drill-down selection when the child changes (a stale area/sub-domain
  // from another child would be meaningless).
  useEffect(() => {
    setSelectedArea(null);
    setSelectedSubdomain(null);
  }, [childId]);

  const subdomainLabel = useMemo(() => {
    const map = new Map<string, string>();
    taxonomy?.ehcp_domains.forEach(t => map.set(t.slug, t.label));
    return (slug: string) => map.get(slug) ?? slug;
  }, [taxonomy]);

  // ── Derivations ───────────────────────────────────────────────────────────

  // Per-domain totals (across all indications) → radar axes.
  const radarData = useMemo(() => {
    const totals = new Map<string, number>();
    summary?.ehcp.forEach(r => {
      const d = domainForSlug(r.area);
      if (!d) return;
      totals.set(d.key, (totals.get(d.key) ?? 0) + r.count);
    });
    return SEND_DOMAINS.map(d => ({ domain: d.short, value: totals.get(d.key) ?? 0 }));
  }, [summary]);

  // Per-domain × indication → grouped/stacked bar (the progression story).
  const indicationData = useMemo(() => {
    const byDomain = new Map<string, Record<EhcpIndication, number>>();
    SEND_DOMAINS.forEach(d => byDomain.set(d.key, { strength: 0, emerging: 0, need: 0 }));
    summary?.ehcp.forEach(r => {
      const d = domainForSlug(r.area);
      if (!d || !INDICATIONS.includes(r.indication)) return;
      byDomain.get(d.key)![r.indication] += r.count;
    });
    return SEND_DOMAINS.map(d => {
      const v = byDomain.get(d.key)!;
      const total = v.strength + v.emerging + v.need;
      const f = (n: number) => (measure === 'share' ? pct(n, total) : n);
      return {
        domain: d.short,
        [INDICATION_LABELS.strength]: f(v.strength),
        [INDICATION_LABELS.emerging]: f(v.emerging),
        [INDICATION_LABELS.need]: f(v.need),
      };
    });
  }, [summary, measure]);

  // Weekly stacked timeline — counts per SEND domain per ISO week.
  const weeklyData = useMemo(() => {
    const byWeek = new Map<string, Record<string, number>>();
    summary?.ehcp_by_week?.forEach(r => {
      const d = domainForSlug(r.area);
      if (!d) return;
      const slot = byWeek.get(r.week_start) ?? {};
      slot[d.short] = (slot[d.short] ?? 0) + r.count;
      byWeek.set(r.week_start, slot);
    });
    return Array.from(byWeek.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([week, counts]) => {
        const weekTotal = SEND_DOMAINS.reduce((s, d) => s + (counts[d.short] ?? 0), 0);
        const row: Record<string, number | string> = { week: shortDate(week) };
        SEND_DOMAINS.forEach(d => {
          const c = counts[d.short] ?? 0;
          row[d.short] = measure === 'share' ? pct(c, weekTotal) : c;
        });
        return row;
      });
  }, [summary, measure]);

  // Top sub-domains by observation count, coloured by parent domain.
  const subdomainData = useMemo(() => {
    const totals = new Map<string, number>();
    summary?.ehcp.forEach(r => totals.set(r.area, (totals.get(r.area) ?? 0) + r.count));
    return Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([area, count]) => ({
        label: subdomainLabel(area),
        count,
        fill: domainForSlug(area)?.colour ?? '#7e8a78',
      }));
  }, [summary, subdomainLabel]);

  // ── Drill-down: selected SEND area over time + sub-domain breakdown ─────────

  const selectedDomain = useMemo(
    () => SEND_DOMAINS.find(d => d.key === selectedArea) ?? null,
    [selectedArea],
  );

  // Map a clicked x-axis label (domain `short`) back to its domain key.
  const keyForShort = useMemo(() => {
    const m = new Map<string, string>();
    SEND_DOMAINS.forEach(d => m.set(d.short, d.key));
    return m;
  }, []);

  // Weekly strength/emerging/need counts for the selected area (or a single
  // sub-domain within it when one is picked). Zero-filled across the range.
  const areaWeekly = useMemo(() => {
    if (!selectedArea) return [];
    // Weeks where the child had any enriched post — i.e. they actually attended
    // and were logged. Weeks NOT in this set (half-term, absence, no logging)
    // become null in the series so the trend bridges them rather than dropping.
    // posts_by_week is the precise signal (it marks weeks active even with zero
    // EHCP tags), but fall back to any week that appears in the weekly EHCP
    // series so the chart still plots if that field is absent.
    const activeWeeks = new Set<string>([
      ...(summary?.posts_by_week ?? []).filter(r => r.posts > 0).map(r => r.week_start),
      ...(summary?.ehcp_by_week ?? []).map(r => r.week_start),
    ]);
    const byWeek = new Map<string, Record<EhcpIndication, number>>();
    weekStartsInRange(fromDate, toDate).forEach(w =>
      byWeek.set(w, { strength: 0, emerging: 0, need: 0 }),
    );
    summary?.ehcp_by_week?.forEach(r => {
      if (domainForSlug(r.area)?.key !== selectedArea) return;
      if (selectedSubdomain && r.area !== selectedSubdomain) return;
      if (!INDICATIONS.includes(r.indication)) return;
      const slot = byWeek.get(r.week_start);
      if (slot) slot[r.indication] += r.count;
    });
    return Array.from(byWeek.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([week, v]) => {
        const active = activeWeeks.has(week);
        const total = v.strength + v.emerging + v.need;
        if (measure === 'share') {
          // A defined mix needs at least one observation in this area that week;
          // otherwise there's no ratio to show → null (Line bridges it).
          const ok = active && total > 0;
          return {
            week: shortDate(week),
            [INDICATION_LABELS.strength]: ok ? pct(v.strength, total) : null,
            [INDICATION_LABELS.emerging]: ok ? pct(v.emerging, total) : null,
            [INDICATION_LABELS.need]: ok ? pct(v.need, total) : null,
          };
        }
        // Counts: null on no-activity weeks (bridged); a real zero (attended, but
        // no observation in this area) stays 0.
        return {
          week: shortDate(week),
          [INDICATION_LABELS.strength]: active ? v.strength : null,
          [INDICATION_LABELS.emerging]: active ? v.emerging : null,
          [INDICATION_LABELS.need]: active ? v.need : null,
        };
      });
  }, [selectedArea, selectedSubdomain, summary, fromDate, toDate, measure]);

  // Period totals per sub-domain within the selected area, split by indication —
  // a horizontal stacked bar. Each datum carries its slug for click-to-narrow.
  const areaSubdomains = useMemo(() => {
    if (!selectedArea) return [];
    const bySlug = new Map<string, Record<EhcpIndication, number>>();
    summary?.ehcp.forEach(r => {
      if (domainForSlug(r.area)?.key !== selectedArea) return;
      if (!INDICATIONS.includes(r.indication)) return;
      const slot = bySlug.get(r.area) ?? { strength: 0, emerging: 0, need: 0 };
      slot[r.indication] += r.count;
      bySlug.set(r.area, slot);
    });
    return Array.from(bySlug.entries())
      .map(([slug, v]) => ({
        slug,
        label: subdomainLabel(slug),
        total: v.strength + v.emerging + v.need,
        [INDICATION_LABELS.strength]: v.strength,
        [INDICATION_LABELS.emerging]: v.emerging,
        [INDICATION_LABELS.need]: v.need,
      }))
      .sort((a, b) => b.total - a.total);
  }, [selectedArea, summary, subdomainLabel]);

  const selectArea = (key: string | null) => {
    setSelectedArea(key);
    setSelectedSubdomain(null);
  };

  // ── Exec summary tiles ──────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const totalObs = summary?.ehcp.reduce((s, r) => s + r.count, 0) ?? 0;
    const domainsTouched = new Set(
      (summary?.ehcp ?? []).map(r => domainForSlug(r.area)?.key).filter(Boolean),
    ).size;
    let strengths = 0, needs = 0;
    summary?.ehcp.forEach(r => {
      if (r.indication === 'strength') strengths += r.count;
      else if (r.indication === 'need') needs += r.count;
    });
    return {
      posts: summary?.posts_considered ?? 0,
      totalObs,
      domainsTouched,
      strengths,
      needs,
    };
  }, [summary]);

  const hasData = stats.totalObs > 0;
  const titleScope = selectedChild
    ? `${selectedChild.first_name} ${selectedChild.last_name}`
    : null;

  // Shared axis/tooltip formatting for the comparative (share-aware) charts.
  const isShare = measure === 'share';
  const measureUnit = isShare ? '%' : undefined;
  const measureDomain = isShare ? ([0, 100] as [number, number]) : undefined;
  const tipFormatter = ((v: any) => (isShare ? `${v}%` : v)) as any;

  // ── Render ───────────────────────────────────────────────────────────────

  if (childPicker) {
    return <>{childPicker}</>;
  }

  if (childId == null) {
    return (
      <div className="atc-root">
        <div className="atc-header">
          <div>
            <h2 className="atc-title">Insights</h2>
            <div className="atc-subtitle">EHCP progress across the four SEND areas of need</div>
          </div>
        </div>
        <div className="atc-empty">Pick a child from the sidebar to view their insights.</div>
      </div>
    );
  }

  return (
    <div className="atc-root">
      <div className="atc-header">
        <div>
          <h2 className="atc-title">Insights</h2>
          <div className="atc-subtitle">
            {titleScope ?? 'Selected child'} · {rangeLabel}
          </div>
        </div>
        <div className="atc-spacer" />
        {/* Option toggles use the shared `.pill-toolbar__segment` chrome, the
            same control the Posts toolbar and Reports use. */}
        {view === 'charts' && (
          <div className="pill-toolbar__segment" role="group" aria-label="Measure">
            <button
              type="button"
              className={measure === 'share' ? 'is-active' : ''}
              aria-pressed={measure === 'share'}
              onClick={() => setMeasure('share')}
              title="Show the mix of observations as a percentage — fair to compare regardless of how many posts were logged"
            >
              Share %
            </button>
            <button
              type="button"
              className={measure === 'count' ? 'is-active' : ''}
              aria-pressed={measure === 'count'}
              onClick={() => setMeasure('count')}
              title="Show raw observation counts"
            >
              Counts
            </button>
          </div>
        )}
        {view === 'behaviour' && (
          <div className="pill-toolbar__segment" role="group" aria-label="Behaviour type">
            {([
              { key: 'negative', label: 'Challenging' },
              { key: 'positive', label: 'Appropriate' },
              { key: 'all', label: 'Both' },
            ] as { key: AbcValenceFilter; label: string }[]).map(opt => (
              <button
                key={opt.key}
                type="button"
                className={valence === opt.key ? 'is-active' : ''}
                aria-pressed={valence === opt.key}
                onClick={() => setValence(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
        <div className="pill-toolbar__segment" role="group" aria-label="Timeframe">
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.key}
              type="button"
              className={activeRange.key === opt.key ? 'is-active' : ''}
              aria-pressed={activeRange.key === opt.key}
              onClick={() => setActiveRange(opt)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* View tabs — navigation, deliberately not the segmented btn-groups in
          the header above. Those change a setting inside a view; these change
          which view you're in. */}
      <div className="atc-tabs" role="tablist" aria-label="Insights view">
        {tabs.map((t, i) => (
          <button
            key={t.key}
            id={`atc-tab-${t.key}`}
            type="button"
            role="tab"
            aria-selected={view === t.key}
            aria-controls={`atc-panel-${t.key}`}
            className={`atc-tab${view === t.key ? ' is-active' : ''}`}
            onClick={() => setView(t.key)}
            onKeyDown={(e) => {
              if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
              e.preventDefault();
              const step = e.key === 'ArrowRight' ? 1 : tabs.length - 1;
              const next = tabs[(i + step) % tabs.length];
              setView(next.key);
              document.getElementById(`atc-tab-${next.key}`)?.focus();
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        className="atc-panel"
        role="tabpanel"
        id={`atc-panel-${view}`}
        aria-labelledby={`atc-tab-${view}`}
      >
      {view === 'targets' ? (
        <TargetGapAnalysis childId={childId} childName={titleScope} fromDate={fromDate} toDate={toDate} />
      ) : view === 'behaviour' ? (
        <BehaviourInsights
          childId={childId}
          childName={titleScope}
          fromDate={fromDate}
          toDate={toDate}
          valence={valence}
          bucket={behaviourBucket}
          taxonomy={taxonomy}
        />
      ) : (
      <>
      <div className="atc-summary">
        <div className="atc-stat">
          <div className="atc-stat__value">{stats.posts}</div>
          <div className="atc-stat__label">Posts in range</div>
        </div>
        <div className="atc-stat">
          <div className="atc-stat__value">{stats.totalObs}</div>
          <div className="atc-stat__label">EHCP observations</div>
        </div>
        <div className="atc-stat">
          <div className="atc-stat__value">{stats.domainsTouched}/4</div>
          <div className="atc-stat__label">SEND areas covered</div>
        </div>
        <div className="atc-stat">
          <div className="atc-stat__value">{stats.strengths} / {stats.needs}</div>
          <div className="atc-stat__label">Strengths / needs</div>
        </div>
      </div>

      {loading && !summary ? (
        <div className="atc-empty">Loading insights…</div>
      ) : error ? (
        <div className="atc-empty">Couldn’t load insights. Please try again.</div>
      ) : !hasData ? (
        <div className="atc-empty">
          No AI-analysed observations for {titleScope ?? 'this child'} in this period.
          Publish posts with classroom notes to build their insight profile.
        </div>
      ) : (
        <>
        <div className="atc-grid">
          <ChartCard
            title="Focus profile"
            subtitle="EHCP observations across the four SEND areas of need"
          >
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke={GRID} />
                <AngleAxis dataKey="domain" tick={{ fill: AXIS, fontSize: 12 }} />
                <RadiusAxis tick={{ fill: AXIS, fontSize: 10 }} allowDecimals={false} />
                <Radar
                  name="Observations"
                  dataKey="value"
                  stroke="#26923f"
                  fill="#26923f"
                  fillOpacity={0.4}
                />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Strengths, emerging & needs"
            subtitle={isShare
              ? 'Mix within each area · click an area to explore'
              : 'Observations per area · click an area to explore'}
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={indicationData}
                margin={{ top: 10, right: 16, left: 0, bottom: 8 }}
                onClick={(s: any) => {
                  const k = keyForShort.get(s?.activeLabel);
                  if (k) selectArea(selectedArea === k ? null : k);
                }}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis dataKey="domain" stroke={AXIS} fontSize={11} interval={0} />
                <YAxis stroke={AXIS} fontSize={12} allowDecimals={false} unit={measureUnit} domain={measureDomain} />
                <Tooltip formatter={tipFormatter} />
                <Legend verticalAlign="bottom" height={28} />
                {INDICATIONS.map(ind => (
                  <Bar
                    key={ind}
                    dataKey={INDICATION_LABELS[ind]}
                    stackId="a"
                    fill={INDICATION_COLOURS[ind]}
                  >
                    {indicationData.map((row, i) => (
                      <Cell
                        key={i}
                        fillOpacity={!selectedArea || keyForShort.get(row.domain as string) === selectedArea ? 1 : 0.3}
                      />
                    ))}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Progress over time"
            subtitle={isShare
              ? 'Weekly mix of SEND areas (% of that week’s observations)'
              : 'EHCP observations per week, by SEND area'}
          >
            {weeklyData.length === 0 ? (
              <div className="atc-card__empty">No weekly data in this period.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={weeklyData} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                  <XAxis dataKey="week" stroke={AXIS} fontSize={11} />
                  <YAxis stroke={AXIS} fontSize={12} allowDecimals={false} unit={measureUnit} domain={measureDomain} />
                  <Tooltip formatter={tipFormatter} />
                  <Legend verticalAlign="bottom" height={28} />
                  {SEND_DOMAINS.map(d => (
                    <Bar key={d.key} dataKey={d.short} stackId="w" fill={d.colour} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard
            title="Most-observed skills"
            subtitle="Top sub-domains in this period"
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={subdomainData}
                layout="vertical"
                margin={{ top: 6, right: 28, left: 8, bottom: 6 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                <XAxis type="number" stroke={AXIS} fontSize={12} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="label"
                  stroke={AXIS}
                  fontSize={11}
                  width={150}
                  tick={{ width: 145 }}
                />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {subdomainData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                  <LabelList dataKey="count" position="right" fontSize={11} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {selectedDomain && (
          <div className="ci-detail">
            <div className="ci-detail__bar">
              <span className="ci-detail__crumb">
                Exploring{' '}
                <strong style={{ color: selectedDomain.colour }}>{selectedDomain.label}</strong>
                {selectedSubdomain && <> · <strong>{subdomainLabel(selectedSubdomain)}</strong></>}
              </span>
              {selectedSubdomain && (
                <button type="button" className="btn btn-sm btn-link ci-detail__clear"
                  onClick={() => setSelectedSubdomain(null)}>
                  ← whole area
                </button>
              )}
              <button type="button" className="btn btn-sm btn-link ci-detail__clear"
                onClick={() => selectArea(null)}>
                ✕ close
              </button>
            </div>

            <div className="atc-grid">
              <ChartCard
                title={`${selectedDomain.short} over time`}
                subtitle={(() => {
                  const scope = selectedSubdomain ? subdomainLabel(selectedSubdomain) : 'this area';
                  return isShare
                    ? `Weekly strength / emerging / need mix for ${scope}`
                    : `Weekly observations for ${scope}`;
                })()}
              >
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={areaWeekly} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                    <XAxis dataKey="week" stroke={AXIS} fontSize={11} />
                    <YAxis stroke={AXIS} fontSize={12} allowDecimals={false} unit={measureUnit} domain={measureDomain} />
                    <Tooltip formatter={tipFormatter} />
                    <Legend verticalAlign="bottom" height={28} />
                    {INDICATIONS.map(ind => (
                      <Line
                        key={ind}
                        type="monotone"
                        dataKey={INDICATION_LABELS[ind]}
                        stroke={INDICATION_COLOURS[ind]}
                        strokeWidth={2.5}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard
                title={`${selectedDomain.short} sub-domains`}
                subtitle="Click a skill to narrow the trend"
              >
                {areaSubdomains.length === 0 ? (
                  <div className="atc-card__empty">No sub-domain observations in this period.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={areaSubdomains}
                      layout="vertical"
                      margin={{ top: 6, right: 24, left: 8, bottom: 6 }}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                      <XAxis type="number" stroke={AXIS} fontSize={12} allowDecimals={false} />
                      <YAxis type="category" dataKey="label" stroke={AXIS} fontSize={11} width={150} />
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={28} />
                      {INDICATIONS.map((ind, idx) => (
                        <Bar
                          key={ind}
                          dataKey={INDICATION_LABELS[ind]}
                          stackId="s"
                          fill={INDICATION_COLOURS[ind]}
                          radius={idx === INDICATIONS.length - 1 ? [0, 4, 4, 0] : undefined}
                          onClick={(d: any) => {
                            const slug = (d?.payload?.slug ?? d?.slug) as string | undefined;
                            if (slug) setSelectedSubdomain(selectedSubdomain === slug ? null : slug);
                          }}
                        >
                          {areaSubdomains.map((row, i) => (
                            <Cell
                              key={i}
                              fillOpacity={!selectedSubdomain || row.slug === selectedSubdomain ? 1 : 0.3}
                            />
                          ))}
                        </Bar>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>
          </div>
        )}
        </>
      )}
      </>
      )}
      </div>
    </div>
  );
};

interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

const ChartCard: React.FC<ChartCardProps> = ({ title, subtitle, children }) => (
  <div className="atc-card">
    <div className="atc-card__head">
      <h3 className="atc-card__title">{title}</h3>
      {subtitle && <div className="atc-card__sub">{subtitle}</div>}
    </div>
    <div className="atc-card__body">{children}</div>
  </div>
);

export default ChildInsights;
